import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useGamerAuth } from '../GamerAuthContext';

export default function TournamentSlots({ tournament: initialTournament, onClose }) {
    const [tournament, setTournament] = useState(initialTournament);

    useEffect(() => {
        if (!window.currentDocId || !window.currentDateStr) return;
        const docRef = doc(db, 'dailyMatches', window.currentDocId, 'dates', window.currentDateStr);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const latestMatches = docSnap.data().data || [];
                const updatedMatch = latestMatches.find(m => m.id === initialTournament.id && m.title === initialTournament.title);
                if (updatedMatch) {
                    setTournament(prev => ({ ...prev, ...updatedMatch }));
                }
            }
        });
        return () => unsubscribe();
    }, [initialTournament.id, initialTournament.title]);

    const [isBlinking, setIsBlinking] = useState(false);
    const prevCredentialsRef = useRef({ roomId: tournament.roomId, roomPassword: tournament.roomPassword });

    useEffect(() => {
        const prev = prevCredentialsRef.current;
        const currentRoomId = tournament.roomId;
        const currentPassword = tournament.roomPassword;

        if (
            (currentRoomId && currentRoomId !== prev.roomId && currentRoomId !== 'TBA') || 
            (currentPassword && currentPassword !== prev.roomPassword && currentPassword !== 'TBA')
        ) {
            setIsBlinking(true);
            setTimeout(() => {
                setIsBlinking(false);
            }, 3000);
        }

        prevCredentialsRef.current = { roomId: currentRoomId, roomPassword: currentPassword };
    }, [tournament.roomId, tournament.roomPassword]);

    // Determine structure based on mode and slots
    const mode = tournament.mode.toUpperCase(); // 'SOLO', 'DUO', 'SQUAD'
    const totalSlots = parseInt(tournament.slots.split(' / ')[1]) || 100;

    let slotsPerTeam = 1;
    if (mode === 'DUO') slotsPerTeam = 2;
    if (mode === 'SQUAD') slotsPerTeam = 4;

    const numTeams = Math.ceil(totalSlots / slotsPerTeam);

    const [selectedSlot, setSelectedSlot] = useState(null); // { teamIndex, slotIndex }
    const [myBookedSlots, setMyBookedSlots] = useState([]);
    const [isBooking, setIsBooking] = useState(false);
    const [bookingSuccess, setBookingSuccess] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [rulesLang, setRulesLang] = useState('EN');
    const [revealCredentials, setRevealCredentials] = useState(false);
    const [unlockedRules, setUnlockedRules] = useState(0);

    const [showReadyRulesModal, setShowReadyRulesModal] = useState(false);
    const [readyUnlockedRules, setReadyUnlockedRules] = useState(0);

    const READY_RULES_DATA = [
        {
            en: "Once you are marked READY, you MUST be present in the game room.",
            hi: "READY mark hone ke baad, aapko game room mein mojood hona ZAROORI hai.",
            danger: false
        },
        {
            en: "If you are not found in the game room after marking READY, your readiness will be voided.",
            hi: "Agar aap READY mark karne ke baad game room mein nahi paye gaye, toh aapka ready status cancel ho jayega.",
            danger: false
        },
        {
            en: "Your prize will NOT be finalized if you mark READY but fail to join the room.",
            hi: "Agar aap room join nahi karte par READY mark karte hain, toh aapka prize final nahi hoga.",
            danger: true
        }
    ];

    const hasBooked = myBookedSlots.length > 0;

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedUid, setSelectedUid] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const { gamerUser } = useGamerAuth();
    
    const [linkedAccounts, setLinkedAccounts] = useState([]);

    // UID Popup State
    const [showAddIdPopup, setShowAddIdPopup] = useState(false);
    const [activeSlotId, setActiveSlotId] = useState(1);
    const [newUid, setNewUid] = useState('');
    const [newIgn, setNewIgn] = useState('');
    const [isFetchingUid, setIsFetchingUid] = useState(false);
    const [fetchSuccess, setFetchSuccess] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [fetchFailCount, setFetchFailCount] = useState(0);
    const [isSavingUid, setIsSavingUid] = useState(false);

    const closePopup = () => {
        setShowAddIdPopup(false);
        setNewUid('');
        setNewIgn('');
        setFetchSuccess(false);
        setFetchError('');
        setFetchFailCount(0);
    };

    const handleFetchUid = async () => {
        if (!newUid) return;
        setIsFetchingUid(true);
        setFetchError('');
        setFetchSuccess(false);

        try {
            const response = await fetch(`https://bgmiesports.gridlabsproduction.workers.dev/?uid=${newUid}`);
            const data = await response.json();

            if (data.success && data.username) {
                setNewIgn(data.username);
                setFetchSuccess(true);
                setFetchError('');
                setFetchFailCount(0);
            } else {
                throw new Error("ACCOUNT NOT FOUND OR INVALID UID");
            }
        } catch (error) {
            setFetchError(error.message || 'FAILED TO FETCH UID');
            setFetchSuccess(false);
            setNewIgn('');
            setFetchFailCount(prev => prev + 1);
        } finally {
            setIsFetchingUid(false);
        }
    };

    const handleSaveId = async () => {
        const userId = gamerUser?.uid || gamerUser?.gamerCode;
        if (!userId || !newUid || !newIgn) return;
        setIsSavingUid(true);
        try {
            const newData = {};
            if (activeSlotId === 1) {
                newData.gameUid1 = newUid;
                newData.gameIgn1 = newIgn;
                if (!gamerUser.profileData?.primaryUidSlot) newData.primaryUidSlot = 1;
            } else {
                newData.gameUid2 = newUid;
                newData.gameIgn2 = newIgn;
                if (!gamerUser.profileData?.primaryUidSlot && !gamerUser.profileData?.gameUid1) newData.primaryUidSlot = 2;
            }

            await setDoc(doc(db, 'users', userId), { profileData: newData }, { merge: true });
            
            // Immediately update local state so they appear
            const slotIdStr = activeSlotId === 1 ? 'slot1' : 'slot2';
            setLinkedAccounts(prev => {
                const updated = [...prev];
                const newAcc = { id: slotIdStr, gameUid: newUid, ign: newIgn, title: newIgn };
                if (activeSlotId === 1) {
                    updated[0] = newAcc;
                } else {
                    if (updated.length > 0) updated[1] = newAcc;
                    else updated.push(newAcc);
                }
                return updated;
            });

            setSelectedUid(slotIdStr);

            closePopup();
        } catch (error) {
            console.error("Error saving UID:", error);
        } finally {
            setIsSavingUid(false);
        }
    };

    useEffect(() => {
        const fetchAccounts = async () => {
            if (!gamerUser || !gamerUser.profileData) return;
            try {
                const prof = gamerUser.profileData;
                const accs = [];
                let primarySlot = prof.primaryUidSlot || 1;
                let defaultSelected = null;

                if (prof.gameUid1 && prof.gameIgn1) {
                    accs.push({ id: 'slot1', gameUid: prof.gameUid1, ign: prof.gameIgn1, title: prof.gameIgn1 });
                    if (primarySlot === 1) defaultSelected = 'slot1';
                }
                if (prof.gameUid2 && prof.gameIgn2) {
                    accs.push({ id: 'slot2', gameUid: prof.gameUid2, ign: prof.gameIgn2, title: prof.gameIgn2 });
                    if (primarySlot === 2) defaultSelected = 'slot2';
                }
                setLinkedAccounts(accs);

                if (defaultSelected) {
                    setSelectedUid(defaultSelected);
                } else if (accs.length > 0) {
                    setSelectedUid(accs[0].id);
                }

                // Pre-populate myBookedSlots if this user already booked
                if (tournament.bookedSlots && tournament.bookedSlots.length > 0) {
                    const booked = tournament.bookedSlots.filter(s =>
                        accs.some(a => a.gameUid === s.uid || a.uid === s.uid)
                    );
                    if (booked.length > 0) {
                        setMyBookedSlots(booked);
                    }
                }
            } catch (err) {
                console.error("Error setting accounts:", err);
            }
        };
        fetchAccounts();
    }, [gamerUser, tournament.bookedSlots]);

    const initiateBooking = () => {
        if (tournament.lobbyType === 'CLEAN LOBBY') {
            setUnlockedRules(0);
            setShowRulesModal(true);
        } else {
            setSelectedPlatform(null);
            setShowConfirmModal(true);
        }
    };

    const handleRulesAgree = () => {
        setShowRulesModal(false);
        if (tournament.lobbyType === 'CLEAN LOBBY') {
            setSelectedPlatform(tournament.title?.includes('EMULATOR') ? 'EMULATOR' : 'MOBILE');
        }
        setShowConfirmModal(true);
    };

    const processBooking = async () => {
        setShowConfirmModal(false);
        setIsBooking(true);

        try {
            const userId = gamerUser?.uid || gamerUser?.gamerCode;
            const entryValue = parseInt((tournament.entry || "0").replace(/\D/g, '')) || 0;
            let currentBalance = 0;
            let vaultRef = null;

            if (userId && entryValue > 0) {
                vaultRef = doc(db, 'users', userId, 'transactions', 'vaultData');
                const vaultSnap = await getDoc(vaultRef);
                if (vaultSnap.exists()) {
                    currentBalance = vaultSnap.data().eCoinsBalance || 0;
                }

                if (currentBalance < entryValue) {
                    alert("Insufficient GLPC balance to join this match.");
                    setIsBooking(false);
                    return;
                }
            }

            // Deduct Entry Fee if applicable
            if (vaultRef && entryValue > 0) {
                const newBalance = currentBalance - entryValue;
                await setDoc(vaultRef, { 
                    eCoinsBalance: newBalance,
                    displayCoins: newBalance 
                }, { merge: true });

                // Check and Process Referral logic
                const prof = gamerUser?.profileData || {};
                // Since hasRecharged is not formally tracked yet in context, we check if they have a referrer
                if (prof.referredBy && !prof.hasProcessedReferral) {
                    // Automatically reward referrer upon first paid match
                    try {
                        const usersRef = collection(db, 'users');
                        let q = query(usersRef, where('profileData.referralCode', '==', prof.referredBy));
                        let querySnapshot = await getDocs(q);

                        if (querySnapshot.empty) {
                            q = query(usersRef, where('profileData.playerId', '==', prof.referredBy));
                            querySnapshot = await getDocs(q);
                        }

                        if (!querySnapshot.empty) {
                            const referrerDoc = querySnapshot.docs[0];
                            const referrerId = referrerDoc.id;
                            const rData = referrerDoc.data().profileData || {};
                            
                            const newPending = Math.max(0, (rData.pendingCommission || 0) - 10);
                            const newConfirmed = (rData.confirmedCommission || 0) + 10;
                            
                            await setDoc(doc(db, 'users', referrerId), {
                                profileData: {
                                    pendingCommission: newPending,
                                    confirmedCommission: newConfirmed
                                }
                            }, { merge: true });

                            // Add real coins to referrer's vault
                            const refVault = doc(db, 'users', referrerId, 'transactions', 'vaultData');
                            const refVaultSnap = await getDoc(refVault);
                            const refBalance = refVaultSnap.exists() ? (refVaultSnap.data().eCoinsBalance || 0) : 0;
                            await setDoc(refVault, {
                                eCoinsBalance: refBalance + 10,
                                displayCoins: refBalance + 10
                            }, { merge: true });
                            
                            // Mark user as processed
                            await setDoc(doc(db, 'users', userId), {
                                profileData: { hasProcessedReferral: true }
                            }, { merge: true });
                        }
                    } catch (e) {
                        console.error("Error processing referral:", e);
                    }
                }
            }

            if (tournament.docId && tournament.dateStr && tournament.id) {
                const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', tournament.dateStr);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    let data = snap.data().data || [];
                    const matchIndex = data.findIndex(m => m.id === tournament.id);
                    if (matchIndex !== -1) {
                        let slotsStr = data[matchIndex].slots || "0 / 100";
                        let [filledStr, totalStr] = slotsStr.split(' / ');
                        let filled = parseInt(filledStr) || 0;
                        let total = parseInt(totalStr) || 100;

                        if (filled < total) {
                            filled += 1;
                            data[matchIndex].slots = `${filled} / ${total}`;

                            const selectedUidObj = linkedAccounts.find(u => u.id === selectedUid);
                            const newBooking = {
                                teamIndex: selectedSlot.teamIndex,
                                slotIndex: selectedSlot.slotIndex,
                                uid: selectedUidObj?.gameUid || selectedUidObj?.uid || '',
                                ign: selectedUidObj?.title || selectedUidObj?.ign || selectedUidObj?.gameUid || 'GAMER',
                                platform: selectedPlatform || 'MOBILE'
                            };
                            if (!data[matchIndex].bookedSlots) data[matchIndex].bookedSlots = [];
                            data[matchIndex].bookedSlots.push(newBooking);

                            await setDoc(docRef, { data }, { merge: true });

                            // Increment total matches for user
                            if (userId) {
                                const matchesRef = doc(db, 'users', userId, 'transactions', 'matchesData');
                                const matchesSnap = await getDoc(matchesRef);
                                if (matchesSnap.exists()) {
                                    await setDoc(matchesRef, {
                                        combatRecords: {
                                            totalMatches: (matchesSnap.data().combatRecords?.totalMatches || 0) + 1
                                        }
                                    }, { merge: true });
                                } else {
                                    await setDoc(matchesRef, {
                                        userId,
                                        combatRecords: {
                                            totalMatches: 1,
                                            totalWins: 0,
                                            totalKills: 0,
                                            prestigeLevel: 1,
                                            currentXP: 0,
                                            lifetimeXP: 0,
                                            consecutiveWins: 0
                                        },
                                        tournamentHistory: [],
                                        bountyVault: {
                                            tournamentPayouts: 0,
                                            eliminationBonuses: 0,
                                            peakWinnings: 0,
                                            totalCashouts: 0
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error updating slots or balance:", error);
        }

        setTimeout(() => {
            setIsBooking(false);
            setBookingSuccess(true);
            const selectedUidObj = linkedAccounts.find(u => u.id === selectedUid);
            setMyBookedSlots(prev => [...prev, { ...selectedSlot, uid: selectedUidObj?.gameUid || selectedUidObj?.uid, ign: selectedUidObj?.title || selectedUidObj?.ign || selectedUidObj?.gameUid || 'GAMER', platform: selectedPlatform }]);
            if (typeof window !== 'undefined') {
                localStorage.setItem(`participated_match_${tournament?.id}`, 'true');
                localStorage.setItem(`participated_ign_${tournament?.id}`, selectedUidObj?.title || selectedUidObj?.ign || 'GAMER');
                localStorage.setItem(`participated_uid_${tournament?.id}`, selectedUidObj?.gameUid || selectedUidObj?.uid || '');
            }
            setTimeout(() => {
                setBookingSuccess(false);
                setSelectedSlot(null);
            }, 2000);
        }, 2000);
    };

    const [isMarkingReady, setIsMarkingReady] = useState(false);

    const markAsReady = async () => {
        if (!myBookedSlots.length) return;
        setIsMarkingReady(true);
        try {
            if (tournament.docId && tournament.dateStr && tournament.id) {
                const docRef = doc(db, 'dailyMatches', tournament.docId, 'dates', tournament.dateStr);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    let data = snap.data().data || [];
                    const matchIndex = data.findIndex(m => m.id === tournament.id);
                    if (matchIndex !== -1 && data[matchIndex].bookedSlots) {
                        let updated = false;
                        data[matchIndex].bookedSlots = data[matchIndex].bookedSlots.map(slot => {
                            const isMySlot = myBookedSlots.some(my => my.teamIndex === slot.teamIndex && my.slotIndex === slot.slotIndex && (my.uid === slot.uid || my.gameUid === slot.uid));
                            if (isMySlot && !slot.ready) {
                                updated = true;
                                return { ...slot, ready: true };
                            }
                            return slot;
                        });
                        
                        if (updated) {
                            await setDoc(docRef, { data }, { merge: true });
                            setMyBookedSlots(prev => prev.map(slot => ({ ...slot, ready: true })));
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error marking ready:", error);
        }
        setIsMarkingReady(false);
    };

    const initiateReady = () => {
        setReadyUnlockedRules(0);
        setShowReadyRulesModal(true);
    };

    const handleReadyRulesAgree = () => {
        setShowReadyRulesModal(false);
        markAsReady();
    };

    const RULES_DATA = [
        {
            en: "ID & Pass appears at the top exactly 5 mins before the match gets started.",
            hi: "Room ke ID and Pass, match shuru hone se theek 5 minute pehle match page par reveal honge.",
            danger: false
        },
        {
            en: "In the room, you must sit EXACTLY in the slot you book here. Wrong slot = Instant Kick.",
            hi: "Room mein same slot me baithna hai jo aapne yaha book kiya hai, wrong slot = kick.",
            danger: false
        },
        {
            en: "Sharing the credentials or inviting any friend to the room is strictly prohibited.",
            hi: "Room mein kisi bhi dost ko direct invite karna ya credentials share karna sakth mana hai.",
            danger: false
        },
        {
            en: "This is a clean lobby match, No hack should be used. Any violation results in a PERMANENT BAN.",
            hi: "Clean lobby mein kisi bhi tarah ki hacking nahi karni, pakde jane par permanent ban milega.",
            danger: true
        }
    ];

    let gridClass = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 min-[1680px]:grid-cols-4";
    if (mode === 'DUO') {
        gridClass = "grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 min-[1680px]:grid-cols-8";
    } else if (mode === 'SOLO') {
        gridClass = "grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 min-[1680px]:grid-cols-12";
    }

    useEffect(() => {
        if (window.lenis && typeof window.lenis.scrollTo === 'function') {
            window.lenis.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, []);

    // Auto-hide credentials after 10 seconds
    useEffect(() => {
        let timeout;
        if (revealCredentials) {
            timeout = setTimeout(() => {
                setRevealCredentials(false);
            }, 10000);
        }
        return () => clearTimeout(timeout);
    }, [revealCredentials]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full min-h-screen bg-transparent dark:bg-[#0a0a0f] font-['Monda'] uppercase text-gray-900 dark:text-white"
        >
            <style>{`
                /* Hide scrollbar globally when slots page is active */
                html::-webkit-scrollbar,
                body::-webkit-scrollbar,
                *::-webkit-scrollbar {
                    display: none !important;
                }
                html, body, * {
                    -ms-overflow-style: none !important;  /* IE and Edge */
                    scrollbar-width: none !important;  /* Firefox */
                }
            `}</style>
            <div className="max-w-[120rem] mx-auto p-[5px] md:p-[30px]">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-[10px] md:pb-[15px] mb-[10px] md:mb-[15px] flex-wrap gap-4 md:gap-6">
                    <div className="w-full md:w-auto flex-1">
                        <div className="flex flex-col items-start gap-2 md:gap-4 mb-2 md:mb-4">
                            <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-500 flex items-center gap-1 md:gap-2 transition-colors text-sm md:text-base">
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                Back to Lobby
                            </button>
                            <h2 className="text-xl md:text-5xl font-black tracking-wider text-gray-900 dark:text-white flex items-center gap-1 md:gap-3 text-left">
                                {tournament.title} <span className="text-pink-600 dark:text-pink-500">SLOTS</span>
                            </h2>
                        </div>
                        <div className="hidden md:flex flex-wrap gap-3 mt-3">
                            <span className={`px-3 py-1 text-sm font-bold rounded-none uppercase tracking-widest ${tournament.lobbyType === 'HACKER vs HACKER' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-green-500/10 text-green-500 border border-green-500/20'}`}>
                                {tournament.lobbyType || 'CLEAN LOBBY'}
                            </span>
                            <span className="bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 px-3 py-1 text-sm font-bold rounded-none border border-cyan-500/20">
                                {tournament.title?.includes('EMULATOR') ? 'EMULATOR LOBBY' : 'MOBILE LOBBY'}
                            </span>
                            <span className="bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 px-3 py-1 text-sm font-bold rounded-none border border-purple-500/20">{mode}</span>
                            <span className="bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 px-3 py-1 text-sm font-bold rounded-none border border-blue-500/20">{tournament.map}</span>
                        </div>
                    </div>

                    <div className="flex flex-row md:flex-col items-center md:items-end gap-2 md:gap-4 w-full md:max-w-[280px]">
                        {/* Credentials Section */}
                        <div
                            className={`relative flex-1 flex flex-col items-center justify-center p-2 md:p-3 rounded-[50px] border-2 bg-gray-50 dark:bg-[#1a1a24] overflow-hidden cursor-pointer w-full transition-all duration-300 ${hasBooked ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${isBlinking ? 'border-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.8)] animate-pulse' : 'border-gray-300 dark:border-gray-600'}`}
                            onClick={() => {
                                if (hasBooked) setRevealCredentials(!revealCredentials);
                            }}
                        >
                            <div className={`flex justify-around w-full px-2 md:px-4 transition-all duration-300 ${!revealCredentials ? 'blur-sm select-none opacity-50' : 'opacity-100'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="text-[8px] md:text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-0.5 md:mb-1">ROOM ID</span>
                                    <span className="text-sm md:text-lg font-black text-gray-900 dark:text-white font-mono tracking-wider">{tournament.roomId || 'TBA'}</span>
                                </div>
                                <div className="w-px bg-gray-200 dark:bg-white/10 mx-1 md:mx-0"></div>
                                <div className="flex flex-col items-center">
                                    <span className="text-[8px] md:text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-0.5 md:mb-1">PASSWORD</span>
                                    <span className="text-sm md:text-lg font-black text-pink-500 font-mono tracking-wider">{tournament.roomPassword || 'TBA'}</span>
                                </div>
                            </div>

                            {!revealCredentials && (
                                <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/30 dark:bg-black/30">
                                    <div className="bg-pink-500 text-white px-2 py-1 md:px-4 md:py-1.5 rounded-[50px] text-[8px] md:text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
                                        <svg className="w-2.5 h-2.5 md:w-3 md:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                        REVEAL
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Booking Section */}
                        {hasBooked ? (
                            myBookedSlots[0]?.ready ? (
                                <button disabled className="flex-1 w-full bg-green-500 text-white px-2 py-2 md:px-4 md:py-3 text-[10px] sm:text-base font-bold rounded-[50px] border border-transparent transition-all flex items-center justify-center gap-1 md:gap-2 cursor-not-allowed whitespace-nowrap h-[44px] md:h-auto">
                                    SECURED {mode === 'SOLO' ? `SLOT ${myBookedSlots[0].teamIndex + 1}` : `T${myBookedSlots[0].teamIndex + 1} S${myBookedSlots[0].slotIndex + 1}`}
                                </button>
                            ) : (
                                !tournament.isReadyCheckActive ? (
                                    <button disabled className="flex-1 w-full bg-gray-600 text-gray-300 px-2 py-2 md:px-4 md:py-3 text-[10px] sm:text-base font-bold rounded-[50px] border border-transparent transition-all flex items-center justify-center gap-1 md:gap-2 cursor-not-allowed whitespace-nowrap h-[44px] md:h-auto opacity-70">
                                        WAITING FOR ADMIN...
                                    </button>
                                ) : (
                                    <button 
                                        onClick={initiateReady}
                                        disabled={isMarkingReady}
                                        className={`flex-1 w-full px-2 py-2 md:px-4 md:py-3 text-[10px] sm:text-base font-bold rounded-[50px] border border-transparent transition-all flex items-center justify-center gap-1 md:gap-2 whitespace-nowrap h-[44px] md:h-auto ${
                                            isMarkingReady 
                                            ? 'bg-blue-500 opacity-80 cursor-wait text-white' 
                                            : 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.5)] animate-pulse'
                                        }`}
                                    >
                                        {isMarkingReady ? 'MARKING...' : 'I AM READY'}
                                    </button>
                                )
                            )
                        ) : selectedSlot ? (
                            <button
                                onClick={initiateBooking}
                                disabled={isBooking || bookingSuccess}
                                className={`flex-1 w-full px-2 py-2 md:px-4 md:py-3 text-[10px] sm:text-base font-bold rounded-[50px] border border-transparent transition-all flex items-center justify-center gap-1 md:gap-2 whitespace-nowrap h-[44px] md:h-auto ${bookingSuccess
                                    ? 'bg-green-500 text-white cursor-not-allowed'
                                    : isBooking
                                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white opacity-80 cursor-wait'
                                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                                    }`}
                            >
                                {isBooking && (
                                    <svg className="animate-spin -ml-1 mr-1 md:mr-2 h-3 w-3 md:h-5 md:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {bookingSuccess
                                    ? `SECURED ${mode === 'SOLO' ? `SLOT ${selectedSlot.teamIndex + 1}` : `T${selectedSlot.teamIndex + 1} S${selectedSlot.slotIndex + 1}`}`
                                    : isBooking
                                        ? `BOOKING ${mode === 'SOLO' ? `SLOT ${selectedSlot.teamIndex + 1}` : `T${selectedSlot.teamIndex + 1} S${selectedSlot.slotIndex + 1}`}`
                                        : `BOOK ${mode === 'SOLO' ? `S${selectedSlot.teamIndex + 1}` : `T${selectedSlot.teamIndex + 1} S${selectedSlot.slotIndex + 1}`}`
                                }
                            </button>
                        ) : (
                            <button disabled className="flex-1 w-full bg-gray-200 dark:bg-[#1a1a24] text-gray-400 dark:text-gray-500 px-2 py-2 md:px-4 md:py-3 text-[10px] sm:text-base font-bold rounded-[50px] transition-all flex items-center justify-center gap-1 md:gap-2 cursor-not-allowed border border-gray-300 dark:border-white/10 whitespace-nowrap h-[44px] md:h-auto">
                                SELECT A SLOT
                            </button>
                        )}
                    </div>
                </div>

                {/* Slots Grid */}
                <div className={`flex-1 grid ${gridClass} gap-[5px] md:gap-[15px] pb-20`}>
                    {Array.from({ length: numTeams }).map((_, teamIndex) => (
                        <div
                            key={teamIndex}
                            className="uppercase font-['Monda'] relative overflow-hidden rounded-none p-[2px] group h-full"
                        >
                            <div className="absolute inset-[-150%] transform-gpu will-change-transform opacity-0 group-hover:opacity-100 group-hover:animate-[spin_4s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0%,#a855f7_25%,#ec4899_50%,#a855f7_75%,transparent_100%)] transition-opacity duration-500" />

                            <div className="relative z-10 w-full h-full bg-white dark:bg-[#1a1a24]">
                                <div className="p-[15px] h-full flex flex-col justify-center">
                                    <div className={`flex items-center mb-4 border-b border-gray-200 dark:border-white/5 pb-2 ${mode === 'SOLO' ? 'justify-center' : 'justify-between'}`}>
                                        <h3 className="text-xl font-black text-gray-900 dark:text-white whitespace-nowrap">
                                            {mode === 'SOLO' ? `SLOT ${teamIndex + 1}` : `TEAM ${teamIndex + 1}`}
                                        </h3>
                                        {mode !== 'SOLO' && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 font-bold ml-2">{slotsPerTeam} SLOTS</span>
                                        )}
                                    </div>

                                    <div
                                        className="w-full grid gap-[15px]"
                                        style={{ gridTemplateColumns: `repeat(${slotsPerTeam}, minmax(0, 1fr))` }}
                                    >
                                        {Array.from({ length: slotsPerTeam }).map((_, slotIndex) => {
                                            const isSelected = selectedSlot?.teamIndex === teamIndex && selectedSlot?.slotIndex === slotIndex;
                                            const dbBookedSlot = (tournament.bookedSlots || []).find(s => s.teamIndex === teamIndex && s.slotIndex === slotIndex);
                                            const myBookedSlotObj = myBookedSlots.find(s => s.teamIndex === teamIndex && s.slotIndex === slotIndex);
                                            const isMyBooked = !!myBookedSlotObj;
                                            const bookedObj = myBookedSlotObj || dbBookedSlot;
                                            const isBooked = !!bookedObj;

                                            // Disable if slot is booked OR if user has already booked any slot in this match
                                            const isDisabled = isBooked || (hasBooked && !myBookedSlotObj);

                                            return (
                                                <button
                                                    key={slotIndex}
                                                    disabled={isDisabled}
                                                    onClick={() => {
                                                        if (!selectedSlot || selectedSlot.teamIndex !== teamIndex) {
                                                            if (window.lenis && typeof window.lenis.scrollTo === 'function') {
                                                                window.lenis.scrollTo(0, { duration: 1.2 });
                                                            } else {
                                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                            }
                                                        }
                                                        setSelectedSlot({ teamIndex, slotIndex });
                                                        setBookingSuccess(false);
                                                    }}
                                                    className={`relative w-full aspect-square rounded-none border-2 overflow-hidden transition-all flex flex-col items-center justify-center group/slot
                                                        ${isMyBooked
                                                            ? 'bg-pink-500/10 border-pink-500 cursor-not-allowed'
                                                            : dbBookedSlot
                                                                ? 'bg-gray-200 dark:bg-[#0a0a0f] border-red-500/30 cursor-not-allowed'
                                                                : isSelected
                                                                    ? 'bg-pink-50 dark:bg-pink-900/20 border-pink-500'
                                                                    : hasBooked
                                                                        ? 'bg-gray-50 dark:bg-[#111] border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed'
                                                                        : 'bg-gray-50 dark:bg-[#111] border-gray-300 dark:border-gray-600 hover:border-pink-500 hover:text-pink-500'
                                                        }
                                                    `}
                                                >
                                                    {isMyBooked || isSelected ? (
                                                        <>
                                                            <img src="https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=150&q=80" alt="You" className={`w-full h-full object-cover ${isSelected && !isMyBooked ? 'opacity-70' : ''}`} />
                                                            {bookedObj?.ready && (
                                                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-10">
                                                                    <svg className="w-8 h-8 text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.8)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                </div>
                                                            )}
                                                            <div className="absolute bottom-0 w-full bg-pink-500/90 text-white text-[8px] font-black tracking-widest text-center py-0.5 uppercase truncate px-1 z-20">{isMyBooked ? myBookedSlotObj.ign : 'SELECTED'}</div>
                                                        </>
                                                    ) : dbBookedSlot ? (
                                                        <>
                                                            <img src="https://images.unsplash.com/photo-1566411520896-01e7ca4726af?auto=format&fit=crop&w=150&q=80" alt="Booked" className="w-full h-full object-cover opacity-80" />
                                                            {bookedObj?.ready && (
                                                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-10">
                                                                    <svg className="w-8 h-8 text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.8)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                </div>
                                                            )}
                                                            <div className="absolute bottom-0 w-full bg-black/60 backdrop-blur-sm text-white text-[8px] font-black tracking-widest text-center py-0.5 uppercase truncate px-1 border-t border-white/10 z-20">{dbBookedSlot.ign}</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-2xl font-light transition-transform group-hover/slot:scale-110 text-gray-400 dark:text-gray-500">+</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {showRulesModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md font-['Monda']"
                        onClick={() => setShowRulesModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-[#1a1a24] p-[15px] rounded-xl border border-gray-200 dark:border-white/10 shadow-[0_0_50px_rgba(239,68,68,0.15)] w-full max-w-lg relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-start border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px]">
                                <div className="flex items-center gap-[15px]">
                                    <div className="p-3 bg-red-500/10 rounded-full">
                                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-wider text-red-500">STRICT GUIDELINES</h3>
                                        <p className="text-[10px] font-bold text-gray-500 tracking-widest">{rulesLang === 'EN' ? 'CLEAN LOBBY RULES & REGULATIONS' : 'CLEAN LOBBY KE SAKHT NIYAM'}</p>
                                    </div>
                                </div>
                                <div className="flex bg-gray-100 dark:bg-black/40 rounded-lg p-1 border border-gray-200 dark:border-white/5 shrink-0">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setRulesLang('EN'); }}
                                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${rulesLang === 'EN' ? 'bg-white dark:bg-gray-800 text-pink-500 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                    >EN</button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setRulesLang('HI'); }}
                                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${rulesLang === 'HI' ? 'bg-white dark:bg-gray-800 text-pink-500 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                    >HI</button>
                                </div>
                            </div>

                            <div className="flex flex-col gap-[15px] mb-[15px]">
                                {RULES_DATA.map((rule, index) => {
                                    const isBlurred = index > unlockedRules;
                                    const isNext = index === unlockedRules + 1;

                                    return (
                                        <div
                                            key={index}
                                            className={`relative flex gap-[15px] items-start p-[15px] rounded-lg border transition-all duration-300 ${rule.danger ? 'bg-red-500/10 border-red-500/30' : 'bg-gray-50 dark:bg-black/30 border-gray-100 dark:border-white/5'
                                                } ${isBlurred ? 'cursor-pointer' : ''}`}
                                            onClick={() => {
                                                if (isBlurred && isNext) {
                                                    setUnlockedRules(index);
                                                }
                                            }}
                                        >
                                            <span className={`${rule.danger ? 'text-red-500' : 'text-pink-500'} font-black text-lg mt-[-2px] transition-opacity duration-300 ${isBlurred ? 'opacity-0' : 'opacity-100'}`}>{index + 1}</span>
                                            <p className={`text-sm font-bold text-justify transition-opacity duration-300 ${rule.danger ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'} ${isBlurred ? 'opacity-0' : 'opacity-100'}`}>
                                                {rulesLang === 'EN' ? rule.en : rule.hi}
                                            </p>

                                            <AnimatePresence>
                                                {isBlurred && (
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-black/60 backdrop-blur-[3px] rounded-lg border border-white/20 dark:border-white/5 overflow-hidden"
                                                    >
                                                        {isNext ? (
                                                            <div className="flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider animate-bounce">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
                                                                TAP TO READ & AGREE
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/80 px-4 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                                                LOCKED
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex gap-[15px]">
                                <button onClick={() => setShowRulesModal(false)} className="flex-1 py-3 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-black uppercase text-sm rounded-[50px] hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
                                    {rulesLang === 'EN' ? 'DECLINE' : 'CANCEL'}
                                </button>
                                <button
                                    onClick={handleRulesAgree}
                                    disabled={unlockedRules < 3}
                                    className={`flex-[2] py-3 font-black uppercase text-sm rounded-[50px] transition-all duration-300 ${unlockedRules < 3 ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600 shadow-[0_0_20px_rgba(239,68,68,0.3)]'}`}
                                >
                                    {rulesLang === 'EN' ? 'I AGREE & BOOK' : 'MUJHE MANZOOR HAI'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showReadyRulesModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md font-['Monda']"
                        onClick={() => setShowReadyRulesModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-[#1a1a24] p-[15px] rounded-xl border border-gray-200 dark:border-white/10 shadow-[0_0_50px_rgba(239,68,68,0.15)] w-full max-w-lg relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-start border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px]">
                                <div className="flex items-center gap-[15px]">
                                    <div className="p-3 bg-red-500/10 rounded-full">
                                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-wider text-red-500">READY GUIDELINES</h3>
                                        <p className="text-[10px] font-bold text-gray-500 tracking-widest">{rulesLang === 'EN' ? 'IMPORTANT RULES' : 'ZAROORI NIYAM'}</p>
                                    </div>
                                </div>
                                <div className="flex bg-gray-100 dark:bg-black/40 rounded-lg p-1 border border-gray-200 dark:border-white/5 shrink-0">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setRulesLang('EN'); }}
                                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${rulesLang === 'EN' ? 'bg-white dark:bg-gray-800 text-pink-500 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                    >EN</button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setRulesLang('HI'); }}
                                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${rulesLang === 'HI' ? 'bg-white dark:bg-gray-800 text-pink-500 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                    >HI</button>
                                </div>
                            </div>

                            <div className="flex flex-col gap-[15px] mb-[15px]">
                                {READY_RULES_DATA.map((rule, index) => {
                                    const isBlurred = index > readyUnlockedRules;
                                    const isNext = index === readyUnlockedRules + 1;

                                    return (
                                        <div
                                            key={index}
                                            className={`relative flex gap-[15px] items-start p-[15px] rounded-lg border transition-all duration-300 ${rule.danger ? 'bg-red-500/10 border-red-500/30' : 'bg-gray-50 dark:bg-black/30 border-gray-100 dark:border-white/5'
                                                } ${isBlurred ? 'cursor-pointer' : ''}`}
                                            onClick={() => {
                                                if (isBlurred && isNext) {
                                                    setReadyUnlockedRules(index);
                                                }
                                            }}
                                        >
                                            <span className={`${rule.danger ? 'text-red-500' : 'text-pink-500'} font-black text-lg mt-[-2px] transition-opacity duration-300 ${isBlurred ? 'opacity-0' : 'opacity-100'}`}>{index + 1}</span>
                                            <p className={`text-sm font-bold text-justify transition-opacity duration-300 ${rule.danger ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'} ${isBlurred ? 'opacity-0' : 'opacity-100'}`}>
                                                {rulesLang === 'EN' ? rule.en : rule.hi}
                                            </p>

                                            <AnimatePresence>
                                                {isBlurred && (
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-black/60 backdrop-blur-[3px] rounded-lg border border-white/20 dark:border-white/5 overflow-hidden"
                                                    >
                                                        {isNext ? (
                                                            <div className="flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider animate-bounce">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
                                                                TAP TO READ & AGREE
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/80 px-4 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                                                LOCKED
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex gap-[15px]">
                                <button onClick={() => setShowReadyRulesModal(false)} className="flex-1 py-3 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-black uppercase text-sm rounded-[50px] hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
                                    {rulesLang === 'EN' ? 'DECLINE' : 'CANCEL'}
                                </button>
                                <button
                                    onClick={handleReadyRulesAgree}
                                    disabled={readyUnlockedRules < 2}
                                    className={`flex-[2] py-3 font-black uppercase text-sm rounded-[50px] transition-all duration-300 ${readyUnlockedRules < 2 ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600 shadow-[0_0_20px_rgba(59,130,246,0.3)]'}`}
                                >
                                    {rulesLang === 'EN' ? 'CONFIRM I AM READY' : 'MAIN TAIYAR HU'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {showConfirmModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md font-['Monda']"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-[#1a1a24] p-[20px] rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl w-full max-w-lg relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px] text-center">
                                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-wider">CONFIRM DETAILS</h3>
                            </div>

                            <div className="flex flex-col gap-5 mb-[20px]">
                                {/* UID Selection */}
                                <div>
                                    <h4 className="text-base font-black text-gray-900 dark:text-white mb-3 uppercase tracking-widest font-['Monda']">SELECT GAME UID</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[0, 1].map(index => {
                                            const uidObj = linkedAccounts[index];
                                            if (uidObj) {
                                                return (
                                                    <button
                                                        key={uidObj.id}
                                                        onClick={() => setSelectedUid(uidObj.id)}
                                                        className={`p-3 rounded-xl border-2 text-left transition-all uppercase flex flex-col justify-center h-[90px] ${selectedUid === uidObj.id ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-200 dark:border-white/10 hover:border-pink-300 dark:hover:border-white/20'}`}
                                                    >
                                                        <div className="text-[10px] font-bold text-gray-500 mb-1 uppercase tracking-widest">ACCOUNT</div>
                                                        <div className="text-lg font-black text-gray-900 dark:text-white font-mono tracking-wider truncate w-full">{uidObj.gameUid || uidObj.uid}</div>
                                                        <div className="text-[10px] font-bold text-pink-500 mt-1 uppercase tracking-widest truncate w-full">{uidObj.title || uidObj.ign || 'GAMER'}</div>
                                                    </button>
                                                );
                                            } else {
                                                return (
                                                    <div 
                                                        key={`empty-${index}`}
                                                        onClick={() => { 
                                                            setActiveSlotId(index + 1);
                                                            setShowAddIdPopup(true);
                                                        }}
                                                        className="p-3 rounded-xl border border-dashed border-gray-300 dark:border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/10 transition-colors group h-[90px]"
                                                    >
                                                        <span className="text-2xl text-gray-400 dark:text-gray-500 font-light mb-1 group-hover:scale-110 transition-transform leading-none">+</span>
                                                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest group-hover:text-pink-500">ADD UID {index + 1}</span>
                                                    </div>
                                                );
                                            }
                                        })}
                                    </div>
                                </div>

                                {/* Platform Selection */}
                                <div>
                                    <h4 className="text-base font-black text-gray-900 dark:text-white mb-3 uppercase tracking-widest font-['Monda']">
                                        {tournament.lobbyType === 'CLEAN LOBBY' ? 'MUST PLAY ON' : 'PLAYING ON'}
                                    </h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setSelectedPlatform('MOBILE')}
                                            disabled={tournament.lobbyType === 'CLEAN LOBBY' && tournament.title?.includes('EMULATOR')}
                                            className={`p-4 rounded-xl border-2 text-center font-black transition-all uppercase tracking-widest flex items-center justify-center gap-1.5 ${tournament.lobbyType === 'CLEAN LOBBY' && tournament.title?.includes('EMULATOR')
                                                ? 'bg-gray-100 dark:bg-[#111] border-gray-200 dark:border-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                                : selectedPlatform === 'MOBILE'
                                                    ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400'
                                                    : 'border-gray-200 dark:border-white/10 text-gray-500 hover:border-pink-300 dark:hover:border-white/20'
                                                }`}
                                        >
                                            {tournament.lobbyType === 'CLEAN LOBBY' && tournament.title?.includes('EMULATOR') ? (
                                                <>
                                                    <span className="line-through text-red-500">MOBILE</span>
                                                    <span className="text-[9px] sm:text-[10px] text-red-500 whitespace-nowrap mt-[2px]">NOT ALLOWED</span>
                                                </>
                                            ) : 'MOBILE'}
                                        </button>
                                        <button
                                            onClick={() => setSelectedPlatform('EMULATOR')}
                                            disabled={tournament.lobbyType === 'CLEAN LOBBY' && !tournament.title?.includes('EMULATOR')}
                                            className={`p-4 rounded-xl border-2 text-center font-black transition-all uppercase tracking-widest flex items-center justify-center gap-1.5 ${tournament.lobbyType === 'CLEAN LOBBY' && !tournament.title?.includes('EMULATOR')
                                                ? 'bg-gray-100 dark:bg-[#111] border-gray-200 dark:border-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                                : selectedPlatform === 'EMULATOR'
                                                    ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400'
                                                    : 'border-gray-200 dark:border-white/10 text-gray-500 hover:border-pink-300 dark:hover:border-white/20'
                                                }`}
                                        >
                                            {tournament.lobbyType === 'CLEAN LOBBY' && !tournament.title?.includes('EMULATOR') ? (
                                                <>
                                                    <span className="line-through text-red-500">EMULATOR</span>
                                                    <span className="text-[9px] sm:text-[10px] text-red-500 whitespace-nowrap mt-[2px]">NOT ALLOWED</span>
                                                </>
                                            ) : 'EMULATOR'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-[15px]">
                                <button onClick={() => setShowConfirmModal(false)} className="flex-1 py-3 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-black uppercase tracking-widest text-sm rounded-[50px] hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
                                    CANCEL
                                </button>
                                <button
                                    onClick={processBooking}
                                    disabled={!selectedUid || !selectedPlatform || (tournament.lobbyType === 'CLEAN LOBBY' && ((tournament.title?.includes('EMULATOR') && selectedPlatform !== 'EMULATOR') || (!tournament.title?.includes('EMULATOR') && selectedPlatform !== 'MOBILE')))}
                                    className={`flex-[2] py-3 font-black uppercase tracking-widest text-sm rounded-[50px] transition-all duration-300 ${(!selectedUid || !selectedPlatform || (tournament.lobbyType === 'CLEAN LOBBY' && ((tournament.title?.includes('EMULATOR') && selectedPlatform !== 'EMULATOR') || (!tournament.title?.includes('EMULATOR') && selectedPlatform !== 'MOBILE')))) ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed' : 'bg-pink-500 text-white hover:bg-pink-600 shadow-[0_0_20px_rgba(236,72,153,0.4)]'}`}
                                >
                                    CONFIRM & BOOK
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ADD UID POPUP MODAL */}
            {showAddIdPopup && (
                <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-[#1a1a24] p-[15px] rounded-xl border border-gray-200 dark:border-white/10 shadow-2xl max-w-sm w-full font-['Monda']"
                    >
                        <div className="border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px] text-center">
                            <h3 className="font-['Monda'] text-xl font-black text-gray-900 dark:text-white uppercase tracking-wider">ADD NEW BGMI ID</h3>
                        </div>
                        
                        <div className="mb-4">
                            <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">ENTER BGMI UID</label>
                            <input 
                                type="text" 
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={newUid} 
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                    setNewUid(val);
                                    setFetchSuccess(false);
                                    setNewIgn("");
                                }}
                                className="w-full font-black text-lg bg-gray-50 dark:bg-black/50 border border-gray-300 dark:border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-pink-500 transition-colors dark:text-white uppercase"
                                placeholder="E.G. 5123456789"
                            />
                        </div>

                        <div className="mb-6 relative">
                            <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">IN-GAME NAME (IGN)</label>
                            <div className="relative w-full">
                                {isFetchingUid && (
                                    <div className="absolute inset-0 bg-gray-200 dark:bg-white/10 animate-pulse rounded-lg z-10 pointer-events-none"></div>
                                )}
                                <input 
                                    type="text" 
                                    value={newIgn} 
                                    onChange={(e) => setNewIgn(e.target.value)}
                                    readOnly={fetchFailCount < 2 && !fetchSuccess}
                                    className={`w-full font-black text-lg bg-gray-50 dark:bg-black/50 border rounded-lg px-4 py-3 focus:outline-none transition-colors dark:text-white uppercase ${
                                        fetchSuccess ? 'border-green-500 focus:border-green-500' : 
                                        fetchFailCount >= 2 ? 'border-pink-500 focus:border-pink-500' : 'border-gray-300 dark:border-white/10'
                                    } ${fetchFailCount < 2 && !fetchSuccess ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    placeholder={fetchFailCount >= 2 ? "ENTER NAME MANUALLY" : "WAITING FOR FETCH..."}
                                />
                            </div>
                        </div>

                        {fetchError && <p className="text-red-500 text-xs font-bold mb-4 uppercase">{fetchError}</p>}
                        {fetchFailCount >= 2 && !fetchSuccess && (
                            <p className="text-yellow-600 dark:text-yellow-500 text-xs font-bold mb-4 uppercase">MANUAL ENTRY ENABLED AFTER 2 FAILED ATTEMPTS.</p>
                        )}

                        <div className="flex gap-[15px]">
                            <button 
                                onClick={closePopup}
                                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white font-bold text-sm uppercase rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                            >
                                CANCEL
                            </button>
                            {(fetchSuccess || fetchFailCount >= 2) ? (
                                <button 
                                    onClick={handleSaveId}
                                    disabled={!newUid || !newIgn || isSavingUid}
                                    className={`flex-1 px-4 py-3 bg-pink-500 text-white font-bold text-sm uppercase rounded-lg transition-colors flex justify-center items-center gap-2 ${isSavingUid ? 'opacity-70 cursor-not-allowed' : 'hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                                >
                                    {isSavingUid ? "SAVING..." : "SAVE ID"}
                                </button>
                            ) : (
                                <button 
                                    onClick={handleFetchUid}
                                    disabled={isFetchingUid || !newUid}
                                    className="flex-1 px-4 py-3 bg-pink-500 text-white font-bold text-sm uppercase rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                                >
                                    {isFetchingUid ? "FETCHING..." : "FETCH"}
                                </button>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </motion.div>
    );
}
